import React, { useState, useEffect, useCallback } from 'react';
import { subscribePush } from '../../utils/pushSubscription';
import { useAccountI18n } from '../../i18n/accountTranslations';

/* ──────────────────────────────────────────
   PWA 설치 안내 팝업 (iOS / Android / Desktop)
   - 기기 자동 감지
   - iOS: 사파리 공유 → 홈 화면에 추가 애니메이션 가이드
   - Android: beforeinstallprompt 네이티브 or 수동 가이드
   - 이미 설치 or 닫기 시 7일간 미노출
────────────────────────────────────────── */

const DISMISS_KEY = 'pwa_install_dismissed';
const SESSION_KEY = 'pwa_install_seen_session';
const DISMISS_DAYS = 14;

function getPlatform() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  return { isIOS, isAndroid, isStandalone };
}

function isDismissed() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < DISMISS_DAYS * 86400000;
  } catch { return false; }
}

function dismiss() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

export default function PWAInstallPrompt() {
  const { tr } = useAccountI18n();
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [step, setStep] = useState(0); // iOS 가이드 스텝

  // beforeinstallprompt 캡처 (Android/Desktop Chrome)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // 노출 판단
  useEffect(() => {
    const p = getPlatform();
    setPlatform(p);
    if (p.isStandalone) return; // 이미 앱으로 실행 중
    if (isDismissed()) return;
    if (!p.isIOS && !p.isAndroid && !deferredPrompt) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {}
    const t = setTimeout(() => {
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {}
      setShow(true);
    }, 15000);
    return () => clearTimeout(t);
  }, [deferredPrompt]);

  const handleClose = useCallback(() => {
    dismiss();
    setShow(false);
  }, []);

  const handleInstallNative = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      dismiss();
      setShow(false);
      // 앱 설치 후 푸시 알림 구독 시도
      subscribePush();
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  if (!show || !platform) return null;

  // ───── iOS 가이드 ─────
  if (platform.isIOS) {
    return (
      <div style={styles.overlay} onClick={handleClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <button style={styles.closeBtn} onClick={handleClose}>✕</button>
          <div style={styles.iconWrap}>
            <img src="/images/cover1-192.png" alt={tr('pwaAlt')} style={styles.appIcon} />
          </div>
          <h3 style={styles.title}>📱 {tr('pwaIosTitle')}</h3>
          <p style={styles.desc}>{tr('pwaDesc')}</p>

          {step === 0 && (
            <div style={styles.guideBox}>
              <div style={styles.stepBadge}>{tr('step', { count: 1 })}</div>
              <div style={styles.guideContent}>
                <p style={styles.guideText}>{tr('iosShare')}</p>
                <div style={styles.iconDemo}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L12 15M12 2L8 6M12 2L16 6" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 14V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V14" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div style={styles.arrowDown}>↓</div>
                <p style={styles.guideSmall}>{tr('iosShareHint')}</p>
              </div>
              <button style={styles.nextBtn} onClick={() => setStep(1)}>{tr('next')} →</button>
            </div>
          )}

          {step === 1 && (
            <div style={styles.guideBox}>
              <div style={styles.stepBadge}>{tr('step', { count: 2 })}</div>
              <div style={styles.guideContent}>
                <p style={styles.guideText}>{tr('iosAddHome')}</p>
                <div style={styles.fakeMenuItem}>
                  <span style={{ fontSize: 20 }}>➕</span>
                  <span>{tr('addHomeScreen')}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...styles.nextBtn, background: 'var(--glass-bg)' }} onClick={() => setStep(0)}>← {tr('back')}</button>
                <button style={styles.nextBtn} onClick={() => setStep(2)}>{tr('next')} →</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={styles.guideBox}>
              <div style={styles.stepBadge}>{tr('step', { count: 3 })}</div>
              <div style={styles.guideContent}>
                <p style={styles.guideText}>{tr('iosTapAdd')}</p>
                <div style={styles.completeDot}>✅</div>
                <p style={styles.guideSmall}>{tr('iosIconCreated')}</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...styles.nextBtn, background: 'var(--glass-bg)' }} onClick={() => setStep(1)}>← {tr('back')}</button>
                <button style={styles.nextBtn} onClick={handleClose}>{tr('understood')} 👍</button>
              </div>
            </div>
          )}

          <p style={styles.bonus}>🎁 {tr('pwaBonus')}</p>
        </div>
      </div>
    );
  }

  // ───── Android / Desktop ─────
  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeBtn} onClick={handleClose}>✕</button>
        <div style={styles.iconWrap}>
          <img src="/images/cover1-192.png" alt={tr('pwaAlt')} style={styles.appIcon} />
        </div>
        <h3 style={styles.title}>📱 {tr('pwaInstallTitle')}</h3>
        <p style={styles.desc}>
          {tr('pwaDesc')}
        </p>

        {deferredPrompt ? (
          /* 네이티브 install prompt 가능 */
          <button style={styles.installBtn} onClick={handleInstallNative}>
            ⬇️ {tr('installApp')}
          </button>
        ) : (
          /* 수동 가이드 (Samsung Browser 등) */
          <div style={styles.guideBox}>
            <p style={styles.guideText}>
              {tr('browserInstallGuide')}
            </p>
            <div style={styles.fakeMenuItem}>
              <span style={{ fontSize: 20 }}>📲</span>
              <span>{tr('addHomeScreen')}</span>
            </div>
          </div>
        )}

        <p style={styles.bonus}>🎁 {tr('pwaOfflineBonus')}</p>

        <button style={styles.laterBtn} onClick={handleClose}>
          {tr('later')}
        </button>
      </div>
    </div>
  );
}

// ───── Styles ─────
const styles = {
  overlay: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10000,
    pointerEvents: 'none',
    background: 'transparent', backdropFilter: 'none',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
    padding: '0 16px 18px',
    animation: 'fadeIn .3s ease',
  },
  modal: {
    width: 'min(100%, 380px)', maxWidth: 380,
    maxHeight: 'calc(100vh - 36px)',
    overflowY: 'auto',
    pointerEvents: 'auto',
    background: 'rgba(14,17,32,0.96)',
    border: '1px solid rgba(248,242,228,0.16)',
    borderRadius: 12, padding: '18px 18px 14px',
    position: 'relative',
    boxShadow: '0 18px 52px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.08)',
    animation: 'slideUp .4s ease',
  },
  closeBtn: {
    position: 'absolute', top: 12, right: 14,
    background: 'none', border: 'none', color: 'var(--text-secondary, #888)',
    fontSize: 20, cursor: 'pointer', padding: 4,
  },
  iconWrap: {
    display: 'flex', justifyContent: 'center', marginBottom: 16,
  },
  appIcon: {
    width: 56, height: 56, borderRadius: 12,
    boxShadow: '0 4px 20px rgba(198,160,80,0.3)',
  },
  title: {
    textAlign: 'center', fontSize: 16, fontWeight: 800,
    color: 'var(--text-primary, #fff)', marginBottom: 6,
  },
  desc: {
    textAlign: 'center', fontSize: 13,
    color: 'var(--text-secondary, #aaa)', marginBottom: 20,
  },
  guideBox: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 14, padding: 16, marginBottom: 12,
  },
  stepBadge: {
    display: 'inline-block', background: 'var(--accent, #c6a050)',
    color: '#000', fontSize: 11, fontWeight: 800,
    padding: '2px 10px', borderRadius: 20, marginBottom: 10,
  },
  guideContent: {
    textAlign: 'center', marginBottom: 12,
  },
  guideText: {
    fontSize: 15, color: 'var(--text-primary, #fff)',
    lineHeight: 1.5, marginBottom: 12,
  },
  guideSmall: {
    fontSize: 12, color: 'var(--text-tertiary, #777)',
  },
  iconDemo: {
    display: 'flex', justifyContent: 'center', margin: '8px 0',
  },
  arrowDown: {
    textAlign: 'center', fontSize: 24, color: 'var(--accent, #c6a050)',
    animation: 'bounce 1s infinite',
  },
  fakeMenuItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 10, padding: '10px 14px', margin: '8px auto',
    maxWidth: 220, fontSize: 15, fontWeight: 600,
    color: 'var(--text-primary, #fff)',
    border: '1px dashed var(--accent, #c6a050)',
  },
  completeDot: {
    fontSize: 40, textAlign: 'center', margin: '8px 0',
  },
  nextBtn: {
    flex: 1, padding: '10px 0', borderRadius: 10,
    background: 'var(--accent, #c6a050)', color: '#000',
    border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer',
  },
  installBtn: {
    width: '100%', padding: '14px 0', borderRadius: 12,
    background: 'var(--accent, #c6a050)', color: '#000',
    border: 'none', fontWeight: 800, fontSize: 16,
    cursor: 'pointer', marginBottom: 12,
    boxShadow: '0 4px 16px rgba(198,160,80,0.4)',
  },
  bonus: {
    textAlign: 'center', fontSize: 13,
    color: 'var(--accent, #c6a050)', margin: '12px 0 8px',
    fontWeight: 600,
  },
  laterBtn: {
    width: '100%', padding: '10px 0',
    background: 'none', border: '1px solid var(--border, rgba(255,255,255,0.15))',
    borderRadius: 10, color: 'var(--text-tertiary, #777)',
    fontSize: 13, cursor: 'pointer',
  },
};
